"use client";

import "../css/globals.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect } from "react";
import { useCookies } from "react-cookie";
import { useRouter, usePathname } from "next/navigation";
import Script from "next/script";
import { Toaster } from "react-hot-toast";

export default function RootLayout({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  // States
  const router = useRouter();
  const pathname = usePathname();
  const [cookies] = useCookies();
  const queryClient = new QueryClient();

  const userIsAuthenticated = cookies.access_token;

  // Functions
  const getProtectedPath = (path: string): string => {
    if (path === "/profile") {
      return "/profile";
    } else if (path === "/inscriptions") {
      return "/inscriptions";
    }
    return "";
  };
  const getUnprotectedPath = (path: string): string => {
    if (path === "/login") {
      return "/login";
    } else if (path === "/register") {
      return "/register";
    }
    return "";
  };

  // Effects
  useEffect(() => {
    // Dev-only preview of protected pages with mock data (see src/lib/preview.ts).
    const preview =
      process.env.NODE_ENV !== "production" &&
      typeof window !== "undefined" &&
      ["1", "true"].includes(new URLSearchParams(window.location.search).get("preview") ?? "");
    if (!userIsAuthenticated && !preview && pathname === getProtectedPath(pathname)) {
      router.replace("/login");
    } else if (userIsAuthenticated && getUnprotectedPath(pathname)) {
      router.replace("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIsAuthenticated, pathname, cookies.access_token]);

  // Render
  return (
    <QueryClientProvider client={queryClient}>
      <html lang="es">
        <head>
          <link
            rel="preload"
            href="/fonts/AkiraExpanded-SuperBold.otf"
            as="font"
            type="font/otf"
            crossOrigin="anonymous"
          />
          <link
            rel="preload"
            href="/fonts/IntegralCF-Medium.otf"
            as="font"
            type="font/otf"
            crossOrigin="anonymous"
          />
          {/* Google Tag */}
          <Script
            src="https://www.googletagmanager.com/gtag/js?id=G-831669HEC5"
            strategy="afterInteractive"
          />
          <Script
            id="google-analytics"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', 'G-831669HEC5');
              `,
            }}
          />
        </head>
        <body>
          {children}
          <Toaster position="top-right" />
        </body>
      </html>
    </QueryClientProvider>
  );
}
